/**
 * Commentary Data Management Page
 * React version of the Qt C++ RTiData MainWindow
 * Provides tabbed interface for managing different commentary categories
 */

import React, { useState, useEffect } from 'react';
import DriversForm from '@/components/commentary/DriversForm';
import DriverPriceChangesForm from '@/components/commentary/DriverPriceChangesForm';
import PEACCForm from '@/components/commentary/PEACCForm';
import PPACCForm from '@/components/commentary/PPACCForm';
import PSACCForm from '@/components/commentary/PSACCForm';
import PVCACCForm from '@/components/commentary/PVCACCForm';
import ForecastForm from '@/components/commentary/ForecastForm';
import DailySpotForm from '@/components/commentary/DailySpotForm';
import CommentaryDataGrid from '@/components/commentary/CommentaryDataGrid';
import EmailBulletsPanel from '@/components/EmailBulletsPanel';
import PendingChangesModal from '@/components/PendingChangesModal';
import ConfirmationModal from '@/components/ConfirmationModal';
import { useNavigate } from "react-router-dom";
import { Save, FolderOpenIcon, Trash2, List, PlusCircle } from "lucide-react";
import { commentaryService } from '@/services/commentary.service';
import { apiClient } from '@rti/shared';

const CATEGORY_METADATA = {
  drivers: {
    name: 'drivers',
    displayName: 'Drivers',
    description: 'Petrochemical Drivers Commentary (Weekly)',
    frequency: 'weekly' as const,
  },
  peacc: {
    name: 'peacc',
    displayName: 'ACC - PE',
    description: 'Polyethylene Inventory Commentary (Monthly)',
    frequency: 'monthly' as const,
  },
  ppacc: {
    name: 'ppacc',
    displayName: 'ACC - PP',
    description: 'Polypropylene Supply/Demand Commentary (Monthly)',
    frequency: 'monthly' as const,
  },
  psacc: {
    name: 'psacc',
    displayName: 'ACC - PS',
    description: 'Polystyrene Supply/Demand Commentary (Monthly)',
    frequency: 'monthly' as const,
  },
  pvcacc: {
    name: 'pvcacc',
    displayName: 'ACC - PVC',
    description: 'Polyvinylchloride Supply/Demand Commentary (Monthly)',
    frequency: 'monthly' as const,
  },
  forecast: {
    name: 'forecast',
    displayName: 'Forecast',
    description: 'Polymer Forecasts (Quarterly)',
    frequency: 'quarterly' as const,
  },
  daily: {
    name: 'daily',
    displayName: 'Spot Feeds',
    description: 'Daily Feedstock Spot Prices & Contract Data',
    frequency: 'daily' as const,
  },
};

const CommentaryDataPage: React.FC = () => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [viewMode, setViewMode] = useState<'grid' | 'form'>('grid');
  const [editingRecord, setEditingRecord] = useState<any | null>(null);
  const [commentaryType, setCommentaryType] = useState<'preliminary' | 'final'>('preliminary');
  const [driversSubView, setDriversSubView] = useState<'commentary' | 'pricechanges'>('commentary');
  const [categoryData, setCategoryData] = useState<{
    [key: string]: any[];
  }>({
    drivers: [],
    driverprices: [],
    peacc: [],
    ppacc: [],
    psacc: [],
    pvcacc: [],
    forecast: [],
    daily: [],
  });
  const [originalPublishedData, setOriginalPublishedData] = useState<{
    [key: string]: any[];
  }>({
    drivers: [],
    driverprices: [],
    peacc: [],
    ppacc: [],
    psacc: [],
    pvcacc: [],
    forecast: [],
    daily: [],
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [pendingChanges, setPendingChanges] = useState<any[]>([]);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [showCancelAllModal, setShowCancelAllModal] = useState(false);
  const [showCancelOneModal, setShowCancelOneModal] = useState(false);
  const [recordToCancel, setRecordToCancel] = useState<any | null>(null);
  const navigate = useNavigate();
  const showButtons = false;

  const categories = [
    { id: 'daily', component: DailySpotForm, ...CATEGORY_METADATA.daily },
    { id: 'drivers', component: DriversForm, ...CATEGORY_METADATA.drivers },
    { id: 'peacc', component: PEACCForm, ...CATEGORY_METADATA.peacc },
    { id: 'ppacc', component: PPACCForm, ...CATEGORY_METADATA.ppacc },
    { id: 'psacc', component: PSACCForm, ...CATEGORY_METADATA.psacc },
    { id: 'pvcacc', component: PVCACCForm, ...CATEGORY_METADATA.pvcacc },
    { id: 'forecast', component: ForecastForm, ...CATEGORY_METADATA.forecast },
    {
      id: 'email_bullets',
      component: null as any,
      name: 'email_bullets',
      displayName: 'Email Bullets',
      description: 'Market bullet summaries for to be sent via RSS email feed',
      frequency: 'periodic' as const,
    },
  ];

  const currentCategory = categories[selectedIndex];

  const transformDriverPriceData = (
    cachedData: any,
    pendingChanges: any[]
  ): any[] => {
    const byDate: Record<string, any> = {};
    const DRIVERS = ['PE', 'PP', 'PS', 'ABS', 'PC', 'PVC', 'PA66', 'PA6', 'PET'];

    let dataObject = cachedData;
    if (Array.isArray(cachedData) && cachedData.length > 0) {
      dataObject = cachedData[0];
    }

    if (dataObject && typeof dataObject === 'object') {
      DRIVERS.forEach(driver => {
        const driverData = dataObject[driver];
        if (Array.isArray(driverData)) {
          driverData.forEach((item: any) => {
            if (item && item.date) {
              const dateStr = String(item.date).trim();
              if (dateStr && dateStr !== 'null' && dateStr !== 'undefined') {
                if (!byDate[dateStr]) {
                  byDate[dateStr] = { date: dateStr };
                  DRIVERS.forEach(d => byDate[dateStr][d] = null);
                }
                byDate[dateStr][driver] = item.value;
              }
            }
          });
        }
      });
    }

    const safePendingChanges = Array.isArray(pendingChanges) ? pendingChanges : [];
    safePendingChanges.forEach(change => {
      if (change && change.date && change.driver) {
        const dateStr = String(change.date).trim();
        if (dateStr && dateStr !== 'null' && dateStr !== 'undefined') {
          if (!byDate[dateStr]) {
            byDate[dateStr] = { date: dateStr };
            DRIVERS.forEach(d => byDate[dateStr][d] = null);
          }
          byDate[dateStr][change.driver] = change.value;
        }
      }
    });

    return Object.values(byDate)
      .filter(row => row.date && row.date !== 'null' && row.date !== 'undefined')
      .sort((a, b) => {
        return b.date.localeCompare(a.date);
      });
  };

  // Load data for current category
  const loadCategoryData = async (categoryId: string) => {
    setLoading(true);
    try {
      let data: any[] = [];
      switch (categoryId) {
        case 'drivers':
          if (driversSubView === 'pricechanges') {
            const cachedData = await apiClient.fetchCachedDriverPriceChangesData();
            const driverPricesPending = await commentaryService.driverPrices.getPending();

            const safePendingChanges = Array.isArray(driverPricesPending) ? driverPricesPending : [];

            if (cachedData) {
              data = transformDriverPriceData(cachedData, safePendingChanges);
            } else {
              data = [];
            }

            setPendingChanges(safePendingChanges);
          } else {
            data = await commentaryService.drivers.getAll();
            const driversPending = await commentaryService.drivers.getPending();
            setPendingChanges(Array.isArray(driversPending) ? driversPending : []);

            const newDriverRecords = (Array.isArray(driversPending) ? driversPending : []).filter(pending => {
              return !data.some(existing =>
                existing.date === pending.date &&
                existing.driverReport === pending.driverReport
              );
            });

            data = [...data, ...newDriverRecords];
          }
          break;
        case 'peacc':
          const cachedPeData = commentaryType === 'final'
            ? await apiClient.fetchCachedACCPEDataFinal()
            : await apiClient.fetchCachedACCPEData();
          data = cachedPeData || [];
          const peaccPending = await commentaryService.peacc.getPending(commentaryType);
          const safePeaccPending = Array.isArray(peaccPending) ? peaccPending : [];
          setPendingChanges(safePeaccPending);

          setOriginalPublishedData(prev => ({
            ...prev,
            peacc: structuredClone(data)
          }));

          data = data.map(prodRecord => {
            const pendingMatch = safePeaccPending.find(p => p.date === prodRecord.date);
            if (pendingMatch) {
              return { ...prodRecord, ...pendingMatch };
            }
            return prodRecord;
          });

          const newPeaccRecords = safePeaccPending.filter(pending => {
            return !data.some(existing => existing.date === pending.date);
          });

          data = [...data, ...newPeaccRecords];
          break;
        case 'ppacc':
          const cachedPpData = commentaryType === 'final'
            ? await apiClient.fetchCachedACCPPDataFinal()
            : await apiClient.fetchCachedACCPPData();
          data = cachedPpData || [];
          const ppaccPending = await commentaryService.ppacc.getPending(commentaryType);
          const safePpaccPending = Array.isArray(ppaccPending) ? ppaccPending : [];
          setPendingChanges(safePpaccPending);

          setOriginalPublishedData(prev => ({
            ...prev,
            ppacc: structuredClone(data)
          }));

          data = data.map(prodRecord => {
            const pendingMatch = safePpaccPending.find(p => p.date === prodRecord.date);
            if (pendingMatch) {
              return { ...prodRecord, ...pendingMatch };
            }
            return prodRecord;
          });

          const newPpaccRecords = safePpaccPending.filter(pending => {
            return !data.some(existing => existing.date === pending.date);
          });

          data = [...data, ...newPpaccRecords];
          break;
        case 'psacc':
          const cachedPsData = commentaryType === 'final'
            ? await apiClient.fetchCachedACCPSDataFinal()
            : await apiClient.fetchCachedACCPSData();
          data = cachedPsData || [];
          const psaccPending = await commentaryService.psacc.getPending(commentaryType);
          const safePsaccPending = Array.isArray(psaccPending) ? psaccPending : [];
          setPendingChanges(safePsaccPending);

          setOriginalPublishedData(prev => ({
            ...prev,
            psacc: structuredClone(data)
          }));

          data = data.map(prodRecord => {
            const pendingMatch = safePsaccPending.find(p => p.date === prodRecord.date);
            if (pendingMatch) {
              return { ...prodRecord, ...pendingMatch };
            }
            return prodRecord;
          });

          const newPsaccRecords = safePsaccPending.filter(pending => {
            return !data.some(existing => existing.date === pending.date);
          });

          data = [...data, ...newPsaccRecords];
          break;
        case 'pvcacc':
          const cachedPvcData = commentaryType === 'final'
            ? await apiClient.fetchCachedACCPVCDataFinal()
            : await apiClient.fetchCachedACCPVCData();
          data = cachedPvcData || [];
          const pvcaccPending = await commentaryService.pvcacc.getPending(commentaryType);
          const safePvcaccPending = Array.isArray(pvcaccPending) ? pvcaccPending : [];
          setPendingChanges(safePvcaccPending);

          setOriginalPublishedData(prev => ({
            ...prev,
            pvcacc: structuredClone(data)
          }));

          data = data.map(prodRecord => {
            const pendingMatch = safePvcaccPending.find(p => p.date === prodRecord.date);
            if (pendingMatch) {
              return { ...prodRecord, ...pendingMatch };
            }
            return prodRecord;
          });

          const newPvcaccRecords = safePvcaccPending.filter(pending => {
            return !data.some(existing => existing.date === pending.date);
          });

          data = [...data, ...newPvcaccRecords];
          break;
        case 'forecast':
          data = await commentaryService.forecast.getAll();
          const pending = await commentaryService.forecast.getPending();
          setPendingChanges(Array.isArray(pending) ? pending : []);
          break;
        case 'daily': {
          const [published, pendingSpot, pendingContracts] = await Promise.all([
            commentaryService.dailySpot.getAll(),
            commentaryService.dailySpot.getPending(),
            commentaryService.dailyContracts.getPending(),
          ]);
          const safePublished = Array.isArray(published) ? published : [];
          const safeSpot = Array.isArray(pendingSpot) ? pendingSpot : [];
          const safeContracts = Array.isArray(pendingContracts) ? pendingContracts : [];

          const byDate: Record<string, any> = {};
          safePublished.forEach(r => { byDate[r.date] = { ...r }; });
          safeSpot.forEach(r => { byDate[r.date] = { ...byDate[r.date], ...r, _pending: true }; });
          safeContracts.forEach(r => {
            if (byDate[r.date]) {
              byDate[r.date] = { ...byDate[r.date], ...r };
            } else {
              byDate[r.date] = { ...r };
            }
          });
          data = Object.values(byDate).sort((a, b) => b.date.localeCompare(a.date));

          const byPending: Record<string, any> = {};
          safeSpot.forEach(r => { byPending[r.date] = { ...r }; });
          safeContracts.forEach(r => { byPending[r.date] = { ...byPending[r.date], ...r, _pending: true }; });
          setPendingChanges(Object.values(byPending).sort((a, b) => b.date.localeCompare(a.date)));
          break;
        }
      }
      setCategoryData((prev) => ({ ...prev, [categoryId]: data }));
    } catch (error) {
      console.error(`Error loading ${categoryId} data:`, error);
    } finally {
      setLoading(false);
    }
  };

  // Load data when category changes
  useEffect(() => {
    if (viewMode === 'grid') {
      loadCategoryData(currentCategory.id);
    }
  }, [selectedIndex, viewMode, commentaryType, driversSubView]);

  const handleEdit = (record: any) => {
    if (currentCategory.id === 'drivers' && driversSubView === 'pricechanges') {
      const DRIVERS = ['PE', 'PP', 'PS', 'ABS', 'PC', 'PVC', 'PA66', 'PA6', 'PET'];
      const changes: Record<string, string | null> = {};
      DRIVERS.forEach(driver => {
        changes[driver] = record[driver] || null;
      });
      setEditingRecord({ date: record.date, changes });
    } else {
      setEditingRecord(record);
    }
    setViewMode('form');
  };

  const handleCreateNew = () => {
    setEditingRecord(null);
    setViewMode('form');
  };

  const handleBackToGrid = () => {
    setEditingRecord(null);
    setViewMode('grid');
    loadCategoryData(currentCategory.id);
  };

  const handleSpotSaved = (spot: any, contracts: any | null) => {
    const pending = contracts
      ? [{ ...spot, ...contracts, _pending: true }]
      : [{ ...spot, _pending: true }];
    setPendingChanges(pending);
    setShowPendingModal(true);
    loadCategoryData('daily');
  };

  const handleSpotEdit = () => {
    setShowPendingModal(false);
  };

  const handleSpotCancelStaging = async () => {
    await commentaryService.dailySpot.cancelAll();
    await commentaryService.dailyContracts.cancelAll();
    setPendingChanges([]);
    setShowPendingModal(false);
    setEditingRecord(null);
    setViewMode('grid');
    loadCategoryData('daily');
  };

  const handleSaveSuccess = async (data?: any) => {
    if (currentCategory.id === 'drivers' && driversSubView === 'pricechanges' && data && Array.isArray(data)) {
      try {
        await commentaryService.driverPrices.upsertBatch(data);
      } catch (error) {
        console.error('Error saving driver price changes:', error);
      }
    } else if (currentCategory.id === 'driverprices' && data && Array.isArray(data)) {
      try {
        await commentaryService.driverPrices.upsertBatch(data);
      } catch (error) {
        console.error('Error saving driver price changes:', error);
      }
    }
    handleBackToGrid();
  };

  const handlePreview = (record: any) => {
    setShowPendingModal(true);
  };

  const handlePublish = async () => {
    try {
      setLoading(true);
      if (currentCategory.id === 'forecast') {
        await commentaryService.forecast.commit();
        await apiClient.fetchForecastCommentary();
      } else if (currentCategory.id === 'drivers') {
        if (driversSubView === 'pricechanges') {
          await commentaryService.driverPrices.commit();
          await apiClient.fetchDriverPriceChangesData();
        } else {
          await commentaryService.drivers.commit();
          await apiClient.fetchDriverData();
        }
      } else if (currentCategory.id === 'peacc') {
        await commentaryService.peacc.commit(commentaryType);
        if (commentaryType === 'final') {
          await apiClient.fetchACCPEDataFinal();
        } else {
          await apiClient.fetchACCPEData();
        }
      } else if (currentCategory.id === 'ppacc') {
        await commentaryService.ppacc.commit(commentaryType);
        if (commentaryType === 'final') {
          await apiClient.fetchACCPPDataFinal();
        } else {
          await apiClient.fetchACCPPData();
        }
      } else if (currentCategory.id === 'psacc') {
        await commentaryService.psacc.commit(commentaryType);
        if (commentaryType === 'final') {
          await apiClient.fetchACCPSDataFinal();
        } else {
          await apiClient.fetchACCPSData();
        }
      } else if (currentCategory.id === 'pvcacc') {
        await commentaryService.pvcacc.commit(commentaryType);
        if (commentaryType === 'final') {
          await apiClient.fetchACCPVCDataFinal();
        } else {
          await apiClient.fetchACCPVCData();
        }
      } else if (currentCategory.id === 'daily') {
        await commentaryService.dailySpot.commit();
        await commentaryService.dailyContracts.commit();
        setEditingRecord(null);
        setViewMode('grid');
      }
      setShowPendingModal(false);
      loadCategoryData(currentCategory.id);
    } catch (error) {
      console.error('Error publishing changes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAll = async () => {
    setShowCancelAllModal(true);
  };

  const confirmCancelAll = async () => {
    try {
      setLoading(true);
      if (currentCategory.id === 'forecast') {
        await commentaryService.forecast.cancelAll();
      } else if (currentCategory.id === 'drivers') {
        if (driversSubView === 'pricechanges') {
          await commentaryService.driverPrices.cancelAll();
        } else {
          await commentaryService.drivers.cancelAll();
        }
      } else if (currentCategory.id === 'driverprices') {
        await commentaryService.driverPrices.cancelAll();
      } else if (currentCategory.id === 'daily') {
        await commentaryService.dailySpot.cancelAll();
        await commentaryService.dailyContracts.cancelAll();
      } else if (currentCategory.id === 'peacc') {
        await commentaryService.peacc.cancelAll(commentaryType);
      } else if (currentCategory.id === 'ppacc') {
        await commentaryService.ppacc.cancelAll(commentaryType);
      } else if (currentCategory.id === 'psacc') {
        await commentaryService.psacc.cancelAll(commentaryType);
      } else if (currentCategory.id === 'pvcacc') {
        await commentaryService.pvcacc.cancelAll(commentaryType);
      }
      setPendingChanges([]);
      loadCategoryData(currentCategory.id);
    } catch (error) {
      console.error('Error canceling changes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelChange = async (record: any) => {
    setRecordToCancel(record);
    setShowCancelOneModal(true);
  };

  const confirmCancelChange = async () => {
    if (!recordToCancel) return;

    try {
      setLoading(true);
      if (currentCategory.id === 'forecast') {
        await commentaryService.forecast.cancelOne(
          recordToCancel.forecastQtrDateId,
          recordToCancel.forecastQtr,
          recordToCancel.polymer
        );
      } else if (currentCategory.id === 'drivers') {
        if (driversSubView === 'pricechanges') {
          await commentaryService.driverPrices.cancelOne(recordToCancel.date);
        } else {
          await commentaryService.drivers.cancelOne(
            recordToCancel.date,
            recordToCancel.driverReport
          );
        }
      } else if (currentCategory.id === 'driverprices') {
        await commentaryService.driverPrices.cancelOne(recordToCancel.date);
      } else if (currentCategory.id === 'daily') {
        await commentaryService.dailySpot.cancelOne(recordToCancel.date);
        await commentaryService.dailyContracts.cancelOne(recordToCancel.date);
      } else if (currentCategory.id === 'peacc') {
        await commentaryService.peacc.cancelOne(recordToCancel.date, commentaryType);
      } else if (currentCategory.id === 'ppacc') {
        await commentaryService.ppacc.cancelOne(recordToCancel.date, commentaryType);
      } else if (currentCategory.id === 'psacc') {
        await commentaryService.psacc.cancelOne(recordToCancel.date, commentaryType);
      } else if (currentCategory.id === 'pvcacc') {
        await commentaryService.pvcacc.cancelOne(recordToCancel.date, commentaryType);
      }
      setRecordToCancel(null);
      loadCategoryData(currentCategory.id);
    } catch (error) {
      console.error('Error canceling change:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAll = () => {
    // TODO: Implement save all functionality
    alert('Save All functionality - to be implemented');
  };

  const handleLoadAll = () => {
    // TODO: Implement load all functionality
    alert('Load All functionality - to be implemented');
  };

  const handleClearAll = () => {
    if (window.confirm('Are you sure you want to clear all data fields?')) {
      // TODO: Implement clear all functionality
      alert('Clear All functionality - to be implemented');
    }
  };

  const ActiveComponent = categories[selectedIndex].component;

  return (
    <div className="commentary-data-page min-h-screen bg-rs-black text-rs-off-white pb-16">
      {/* Header / Menu Bar */}
      <header className="bg-rs-jet border-b border-rs-jet-light px-6 py-4">
        <button className="bg-rs-highlight-gray/35 hover:bg-rs-highlight-gray/70 mb-4" onClick={() => navigate("/dashboard")}>
          ← Back to Dashboard
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-rs-green">
              Commentary Data Management
            </h1>
            <p className="text-sm text-rs-off-white/70 mt-1">
              RTiData - Petrochemical Commentary Management
            </p>
          </div>
          { showButtons && (
          <div className="flex gap-3">
            <button
              onClick={handleSaveAll}
              className="bg-rs-jet-light hover:bg-rs-jet-light/70 text-rs-off-white font-semibold px-4 py-2 rounded transition-colors"
              title="Save All (Ctrl+S)"
            >
              <Save className={'mr-1 text-rs-dark-green'} /> Save All
            </button>
            <button
              onClick={handleLoadAll}
              className="bg-rs-jet-light hover:bg-rs-jet-light/70 text-rs-off-white font-semibold px-4 py-2 rounded transition-colors"
              title="Load All (Ctrl+O)"
            >
              <FolderOpenIcon className="mr-1.5 text-rs-rb-gradient-end"/> Load All
            </button>
            <button
              onClick={handleClearAll}
              className="bg-rs-jet-light hover:bg-rs-jet-light/70 text-rs-off-white font-semibold px-4 py-2 rounded transition-colors"
            >
              <Trash2 className={'mr-1 text-rs-red'} /> Clear All
            </button>
          </div>
          )}
        </div>
      </header>

      {/* Main Content Area with Tabs */}
      <main className="container mx-auto px-6 py-6">
        {/* Tab List */}
        <div className="flex gap-1 bg-rs-jet p-1 rounded-lg mb-6 border border-rs-jet-light">
          {categories.map((category, index) => (
            <button
              key={category.id}
              onClick={() => setSelectedIndex(index)}
              className={`w-full rounded-md py-3 px-4 text-sm font-medium leading-5 transition-all
                ${
                  selectedIndex === index
                    ? 'bg-rs-black text-rs-green shadow border-t-2 border-rs-green font-bold'
                    : 'text-rs-off-white hover:bg-rs-jet-light hover:text-rs-light-green'
                }`}
            >
              <div className="flex flex-col items-center">
                <span>{category.displayName}</span>
                <span className="text-xs opacity-70 mt-1">
                  ({category.frequency})
                </span>
              </div>
            </button>
          ))}
        </div>

        {/* Tab Panel */}
        <div className="rounded-lg bg-rs-black p-6 border border-rs-jet-light">
          {currentCategory.id !== 'email_bullets' && (
          <div className="mb-4 pb-4 border-b border-rs-jet-light flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-rs-green">
                {currentCategory.displayName}
                {currentCategory.id === 'drivers' && driversSubView === 'pricechanges' && (
                  <span className="text-sm text-rs-off-white/70 ml-3">- Price Changes</span>
                )}
                {editingRecord && viewMode === 'form' && (
                  <span className="text-sm text-rs-off-white/70 ml-3">(Editing)</span>
                )}
                {!editingRecord && viewMode === 'form' && (
                  <span className="text-sm text-rs-off-white/70 ml-3">(New Record)</span>
                )}
              </h2>
              <p className="text-sm text-rs-off-white/70 mt-1">
                {currentCategory.id === 'drivers' && driversSubView === 'pricechanges'
                  ? 'Monthly Driver Price Changes (First Friday)'
                  : currentCategory.description}
              </p>
            </div>

            {/* View Mode Toggle */}
            <div className="flex gap-2">
              {viewMode === 'grid' ? (
                <>
                  {currentCategory.id === 'drivers' && (
                    <select
                      value={driversSubView}
                      onChange={(e) => setDriversSubView(e.target.value as 'commentary' | 'pricechanges')}
                      className="bg-rs-jet border border-rs-jet-light rounded px-3 py-2 text-rs-off-white focus:border-rs-green focus:outline-none min-w-[180px]"
                    >
                      <option value="commentary">Drivers Commentary</option>
                      <option value="pricechanges">Driver Price Changes</option>
                    </select>
                  )}
                  <button
                    onClick={handleCreateNew}
                    className="flex items-center gap-2 bg-rs-green hover:bg-rs-light-green !text-rs-off-black font-semibold px-4 py-2 rounded transition-colors"
                  >
                    <PlusCircle size={18} />
                    New Record
                  </button>
                  {['peacc', 'ppacc', 'psacc', 'pvcacc'].includes(currentCategory.id) && (
                    <select
                      value={commentaryType}
                      onChange={(e) => setCommentaryType(e.target.value as 'preliminary' | 'final')}
                      className="bg-rs-jet border border-rs-jet-light rounded px-3 py-2 text-rs-off-white focus:border-rs-green focus:outline-none min-w-[140px]"
                    >
                      <option value="preliminary">Preliminary</option>
                      <option value="final">Final</option>
                    </select>
                  )}
                </>
              ) : (
                <button
                  onClick={handleBackToGrid}
                  className="flex items-center gap-2 bg-rs-jet-light hover:bg-rs-jet-light/70 text-rs-off-white font-semibold px-4 py-2 rounded transition-colors"
                >
                  <List size={18} />
                  Back to List
                </button>
              )}
            </div>
          </div>
          )}

          {/* Grid View or Form View */}
          {currentCategory.id === 'email_bullets' ? (
            <EmailBulletsPanel />
          ) : viewMode === 'grid' ? (
            <CommentaryDataGrid
              categoryId={currentCategory.id}
              data={categoryData[currentCategory.id] || []}
              loading={loading}
              onEdit={handleEdit}
              onRefresh={() => loadCategoryData(currentCategory.id)}
              onPreview={['forecast', 'drivers', 'peacc', 'ppacc', 'psacc', 'pvcacc', 'daily'].includes(currentCategory.id) ? handlePreview : undefined}
              onPublish={['forecast', 'drivers', 'peacc', 'ppacc', 'psacc', 'pvcacc', 'daily'].includes(currentCategory.id) ? handlePublish : undefined}
              onCancelAll={['forecast', 'drivers', 'peacc', 'ppacc', 'psacc', 'pvcacc', 'daily'].includes(currentCategory.id) ? handleCancelAll : undefined}
              pendingChanges={['forecast', 'drivers', 'peacc', 'ppacc', 'psacc', 'pvcacc', 'daily'].includes(currentCategory.id) ? pendingChanges : []}
              onCancelChange={handleCancelChange}
              driversSubView={driversSubView}
            />
          ) : (
            currentCategory.id === 'drivers' && driversSubView === 'pricechanges' ? (
              <DriverPriceChangesForm
                initialData={editingRecord}
                onSave={handleSaveSuccess}
                onCancel={handleBackToGrid}
              />
            ) : currentCategory.id === 'daily' ? (
              <DailySpotForm
                initialData={editingRecord}
                onSave={handleSpotSaved}
                onCancel={handleBackToGrid}
              />
            ) : (
              <ActiveComponent
                initialData={editingRecord}
                onSave={handleSaveSuccess}
                onCancel={handleBackToGrid}
                commentaryType={['peacc', 'ppacc', 'psacc', 'pvcacc'].includes(currentCategory.id) ? commentaryType : undefined}
              />
            )
          )}
        </div>
      </main>

      {/* Status Bar */}
      <footer className="fixed bottom-0 left-0 right-0 bg-rs-jet border-t border-rs-jet-light px-6 py-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-rs-off-white">
            Ready - {categories[selectedIndex].displayName} selected
          </span>
        </div>
      </footer>

      {/* Pending Changes Modal */}
      <PendingChangesModal
        isOpen={showPendingModal}
        onClose={() => setShowPendingModal(false)}
        onCommit={handlePublish}
        onEdit={handleSpotEdit}
        onCancelStaging={handleSpotCancelStaging}
        changeType={currentCategory.id as any}
        pendingData={pendingChanges}
        commentaryType={commentaryType}
        driversSubView={driversSubView}
      />

      {/* Cancel All Confirmation Modal */}
      <ConfirmationModal
        isOpen={showCancelAllModal}
        onClose={() => setShowCancelAllModal(false)}
        onConfirm={confirmCancelAll}
        title="Cancel All Changes"
        message="Are you sure you want to cancel all pending changes? This action cannot be undone and will discard all uncommitted changes."
        confirmText="Yes, Cancel All"
        cancelText="No, Keep Changes"
        isDangerous={true}
      />

      {/* Cancel Individual Change Confirmation Modal */}
      <ConfirmationModal
        isOpen={showCancelOneModal}
        onClose={() => {
          setShowCancelOneModal(false);
          setRecordToCancel(null);
        }}
        onConfirm={confirmCancelChange}
        title="Cancel Pending Change"
        message="Are you sure you want to cancel this pending change? This action cannot be undone."
        confirmText="Yes, Cancel Change"
        cancelText="No, Keep Change"
        isDangerous={true}
      />
    </div>
  );
};

export default CommentaryDataPage;

